import { useCallback, useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgPen, CgTrash } from "react-icons/cg"; // icons for edit/delete
import CustomModal from "../Commaon/CustomModal";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";

const PackagesList = () => {
  const navigate = useNavigate();

  const [filteredpackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [packageTestModel, setPackageTestModel] = useState(false);
  const [packageTestId, setPackageTestId] = useState("");
 
  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const getPackage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/package?page=${pageNo}&limit=${limit}&search=${encodeURIComponent(query.trim())}`,
        { method: "GET" }
      );
      const data = response.data;
      if (data.s === 200) {
        setFilteredPackages(data.r || []);
        setTotalRecords(data.tr || 0);
      }
    } catch (err) {
      console.error("Failed to fetch Packages:", err);
      setSnack({
        notification: { message: "Error fetching Packages", type: "error" },
      });
      setFilteredPackages([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, query]);

  useEffect(() => {
    getPackage();
  }, [getPackage]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const startIndex = (pageNo - 1) * limit;
  const totalPages = Math.ceil(totalRecords / limit) || 1;
  const paginatedCategories = filteredpackages;
  const visibleIds = paginatedCategories.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };


  const deletePackages = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
    setDeleteIds([]);
  };

  const deleteSelectedPackages = () => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one package", type: "error" },
      });
      return;
    }
    setDeleteId(null);
    setDeleteIds(selectedIds);
    setDeleteModel(true);
  };

  const togglePackageStatus = async (item) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/package/${item._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !item.isActive }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Package status updated", type: "success" },
        });
        getPackage();
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating package status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async () => {
    const ids = deleteIds.length ? deleteIds : deleteId ? [deleteId] : [];
    if (!ids.length) return;

    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/package", {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: data.m || "Package deleted successfully",
            type: "success",
          },
        });
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        const remainingRecords = Math.max(totalRecords - ids.length, 0);
        const lastPage = Math.ceil(remainingRecords / limit) || 1;
        if (pageNo > lastPage) {
          setPageNo(lastPage);
        } else {
          getPackage();
        }
      } else {
        throw new Error(data.m || "Error deleting package");
      }
    } catch (err) {
      console.error("Error deleting package:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting package",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
      setDeleteIds([]);
    }
  };

  const handleDeletePackageTest = async (e) => {
    e.preventDefault();
    if (!packageTestId.trim()) {
      setSnack({
        notification: { message: "Package test id is required", type: "error" },
      });
      return;
    }
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/package-test/${packageTestId}`, {
        method: "DELETE",
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Package test deleted successfully", type: "success" },
        });
        setPackageTestModel(false);
        setPackageTestId("");
        getPackage();
      } else {
        setSnack({
          notification: { message: response?.data?.m || "Error deleting package test", type: "error" },
        });
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error deleting package test", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        {/* Header + Page Size */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Packages List</h2>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm "
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-custom btn-sm"
              onClick={() => setPackageTestModel(true)}
            >
              Remove Package
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control"
              style={{ width: "40vw" }}
              placeholder="Search packages..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn-outline-custom">Search</button>
          </div>
          <button
            className="btn btn-outline-custom"
            onClick={() => navigate("/packages/add")}
          >
            Add Packages
          </button>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">{selectedIds.length} selected</span>
          <button className="btn btn-sm btn-outline-danger" onClick={deleteSelectedPackages}>
            Delete
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all packages on this page"
                  />
                </th>
                <th>Id</th>
                <th>title</th>
                <th>description</th>
                <th>originalPrice</th>
                <th>sampleType</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading &&
                paginatedCategories.length > 0 &&
                paginatedCategories.map((c, idx) => (
                  <tr key={c._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(c._id)}
                        onChange={() => toggleRowSelection(c._id)}
                        aria-label={`Select ${c.title || "package"}`}
                      />
                    </td>
                    <td className="fw-semibold">{startIndex + idx + 1}</td>
                    <td>{c.title || "-"}</td>
                    <td>{c.description || "-"}</td>
                    <td>{c.originalPrice || "-"}</td>
                    <td>{c.sampleType || "-"}</td>
                    <td>
                      {c.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => togglePackageStatus(c)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => togglePackageStatus(c)}>Inactive</span>
                      )}
                    </td>
                    
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(`/packages/view/${c._id}`)}
                        >
                          <CgEye size={16} />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/packages/edit/${c._id}`)}
                        >
                          <CgPen size={16} />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deletePackages(c._id)}
                        >
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedCategories.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {totalPages}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.min(prev + 1, totalPages))}
            disabled={pageNo >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={packageTestModel} onHide={() => setPackageTestModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Package Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleDeletePackageTest}>
            <div className="mb-3">
              <label className="form-label">Package Test ID</label>
              <input
                className="form-control"
                value={packageTestId}
                onChange={(e) => setPackageTestId(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setPackageTestModel(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-outline-danger">
                Delete
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Delete Package Modal */}
      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message={`This action can not be reversed. Are you sure you want to delete ${deleteIds.length || 1} package${(deleteIds.length || 1) > 1 ? "s" : ""}?`}
        onConfirm={() => {
          handleDeletePackage();
          setDeleteModel(false);
        }}
        onCancel={() => {
          setDeleteModel(false);
          setDeleteId(null);
          setDeleteIds([]);
        }}
      />
    </>
  );
};

export default PackagesList;
